import { PencilIcon } from "lucide-react";
import { useState } from "react";
import { SubmitHandler } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { useMutation } from "urql";

import { GameForm } from "@/components/forms/GameForm";
import { GameInput, zGameInput } from "@/components/forms/validation/game";
import { buttonVariants } from "@/components/ui/button";
import { DestructiveConfirmationAlertDialog } from "@/components/ui/destructive-confirmation-alert-dialog";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { graphql } from "@/gen";
import { GameT } from "@/routes/game/queries";
import { cn } from "@/utils";

const updateGameMutation = graphql(`
    mutation updateGame($input: UpdateGameInput!) {
        updateGame(input: $input) {
            id
            ...useGame_Game
        }
    }
`);

const deleteGameMutation = graphql(`
    mutation deleteGame($gameId: UUID!) {
        deleteGame(gameId: $gameId)
    }
`);

export const GameEditDialog = () => {
    const [_res, updateGame] = useMutation(updateGameMutation);
    const [_resDelete, deleteGame] = useMutation(deleteGameMutation);
    const { game } = useOutletContext<{
        game: GameT;
    }>();

    const [isOpen, setIsOpen] = useState(false);
    const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);

    const onSubmit: SubmitHandler<GameInput> = async input => {
        const result = await updateGame({
            input: {
                id: game.id,
                player1Color: input.player1Color,
                player2Color: input.player2Color,
                startingPositionFen: input.startingPositionFen,
                startedAt: input.startedAt ? input.startedAt.toISOString() : null,
                scheduledStartAt: input.scheduledStartAt.toISOString(),
                result: input.result,
                finishedAt: input.finishedAt ? input.finishedAt.toISOString() : null,
                heroImageId: input.heroImageId,
                pgnRoundTag: input.pgnRoundTag,
            },
        });

        if (result.error) {
            toast.error("Unable to update game", { description: result.error.message });
            for (const error of result.error.graphQLErrors) {
                // @ts-expect-error typed as string from the API
                form.setError(error.name, { message: error.message });
            }
        } else {
            toast.success("Game was updated");
            setIsOpen(false);
        }
    };

    const handleDelete = async () => {
        const result = await deleteGame({ gameId: game.id });

        if (result.error) {
            toast.error("Unable to delete game", {
                description: result.error.message,
            });
        } else {
            toast.success("Game was deleted");
            setIsOpen(false);
        }
    };

    const onDelete = () => {
        setDeleteAlertOpen(true);
    };

    const defaultValues = zGameInput.parse(game);

    return (
        <>
            <Dialog open={isOpen} onOpenChange={() => setIsOpen(!isOpen)}>
                <DialogTrigger className={cn(buttonVariants())}>
                    <PencilIcon />
                </DialogTrigger>
                <DialogContent
                    className="max-h-[80vh] w-full max-w-3xl overflow-y-auto"
                    aria-describedby={undefined}
                >
                    <DialogHeader>
                        <div className="mr-4 flex items-center justify-between">
                            <DialogTitle>Edit game</DialogTitle>
                        </div>
                    </DialogHeader>
                    <GameForm
                        onSubmit={onSubmit}
                        defaultValues={defaultValues}
                        onDelete={onDelete}
                    />
                </DialogContent>
            </Dialog>

            <DestructiveConfirmationAlertDialog
                open={deleteAlertOpen}
                onOpenChange={setDeleteAlertOpen}
                onConfirm={handleDelete}
                title="Delete Game"
                description="Are you sure you want to delete this game? This action cannot be undone."
            />
        </>
    );
};
